import { useState, useMemo, useEffect } from 'react'
import type { AddCardRequest, BillingInfo, BillingCountry } from '@/types/payment-methods'
import { BILLING_COUNTRIES, COUNTRY_ADDRESS_CONFIG } from '@/types/payment-methods'
import { PaymentIcon, getCardType, type PaymentType } from 'react-svg-credit-card-payment-icons'
import { FloatingInput } from '@/components/ui/floating-input'
import { useUser } from '@/hooks/use-user'
import { usePaymentMethods } from '@/hooks/use-payment-methods'

interface DirectCardFormProps {
	onSubmit: (data: AddCardRequest) => void
	onError: (message: string) => void
	isSubmitting: boolean
	isUpdate?: boolean
	/** Form ID for external submit buttons */
	formId?: string
}

/** Format a raw digit string into card number with spaces every 4 digits */
function formatCardNumber(value: string): string {
	const digits = value.replace(/\D/g, '').slice(0, 19)
	return digits.replace(/(.{4})/g, '$1 ').trim()
}

/** Format expiration as MM / YY */
function formatExpiry(value: string): string {
	const digits = value.replace(/\D/g, '').slice(0, 4)
	if (digits.length > 2) return `${digits.slice(0, 2)} / ${digits.slice(2)}`
	return digits
}

/** Format postal/zip input based on country */
function formatPostalCode(value: string, country: BillingCountry): string {
	if (country === 'CA') {
		// Canadian postal code: A1A 1A1 (letter-digit alternating)
		const clean = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6)
		if (clean.length > 3) return `${clean.slice(0, 3)} ${clean.slice(3)}`
		return clean
	}
	if (country === 'MX') {
		return value.replace(/\D/g, '').slice(0, 5)
	}
	// US: digits and hyphen for ZIP+4
	return value.replace(/[^\d-]/g, '').slice(0, 10)
}

export default function DirectCardForm({
	onSubmit,
	onError,
	isSubmitting,
	formId = 'direct-card-form',
}: DirectCardFormProps) {
	const { email, displayName } = useUser()
	const { cards } = usePaymentMethods()

	// Parse user's display name for billing fallback
	const nameParts = (displayName || '').split(' ')
	const userFirstName = nameParts[0] || ''
	const userLastName = nameParts.slice(1).join(' ') || ''

	// Card fields
	const [cardNumber, setCardNumber] = useState('')
	const [expiry, setExpiry] = useState('')
	const [cvv, setCvv] = useState('')

	// Billing fields
	const [billing, setBilling] = useState<BillingInfo>({
		firstName: userFirstName,
		lastName: userLastName,
		address1: '',
		address2: '',
		city: '',
		state: '',
		zip: '',
		country: 'US',
	})
	const [hasPrepopulated, setHasPrepopulated] = useState(false)

	// Prepopulate billing from the default card (or first card with billing data)
	useEffect(() => {
		if (hasPrepopulated || cards.length === 0) return
		const source = cards.find((c) => c.isDefault)?.billing ?? cards[0]?.billing
		if (!source) return
		setBilling({
			firstName: source.firstName || userFirstName,
			lastName: source.lastName || userLastName,
			address1: source.address1 || '',
			address2: source.address2 || '',
			city: source.city || '',
			state: source.state || '',
			zip: source.zip || '',
			country: source.country || 'US',
		})
		setHasPrepopulated(true)
	}, [cards, hasPrepopulated])

	// Detect card brand as user types
	const detectedBrand: PaymentType = useMemo(() => {
		const digits = cardNumber.replace(/\D/g, '')
		if (digits.length < 2) return 'Generic' as PaymentType
		return getCardType(digits)
	}, [cardNumber])

	const updateBilling = (field: keyof BillingInfo, value: string) => {
		setBilling((prev) => ({ ...prev, [field]: value }))
	}

	const handleCountryChange = (country: BillingCountry) => {
		setBilling((prev) => ({ ...prev, country, state: '', zip: '' }))
	}

	// Country-specific address config
	const addrConfig = COUNTRY_ADDRESS_CONFIG[billing.country || 'US']

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault()

		const ccDigits = cardNumber.replace(/\D/g, '')
		const expDigits = expiry.replace(/\D/g, '')
		const cvvDigits = cvv.replace(/\D/g, '')

		// Validate card fields
		if (ccDigits.length < 13 || ccDigits.length > 19) {
			onError('Please enter a valid card number.')
			return
		}
		if (expDigits.length !== 4) {
			onError('Please enter a valid expiration date (MM/YY).')
			return
		}
		const expMonth = parseInt(expDigits.slice(0, 2), 10)
		if (expMonth < 1 || expMonth > 12) {
			onError('Expiration month must be between 01 and 12.')
			return
		}
		if (cvvDigits.length < 3 || cvvDigits.length > 4) {
			onError('Please enter a valid CVV.')
			return
		}

		// Validate required billing fields
		if (!billing.firstName.trim()) { onError('First name is required.'); return }
		if (!billing.lastName.trim()) { onError('Last name is required.'); return }
		if (!billing.address1.trim()) { onError('Address is required.'); return }
		if (!billing.city.trim()) { onError('City is required.'); return }
		if (!billing.state.trim()) { onError(`${addrConfig.stateLabel} is required.`); return }
		if (!billing.zip.trim()) { onError(`${addrConfig.postalLabel} is required.`); return }

		const cleanBilling: BillingInfo = {
			firstName: billing.firstName.trim(),
			lastName: billing.lastName.trim(),
			address1: billing.address1.trim(),
			city: billing.city.trim(),
			state: billing.state.trim(),
			zip: billing.zip.trim(),
			country: billing.country || 'US',
		}
		if (billing.address2?.trim()) cleanBilling.address2 = billing.address2.trim()
		// Attach user's email automatically
		if (email) cleanBilling.email = email

		onSubmit({
			ccNumber: ccDigits,
			ccExp: expDigits,
			cvv: cvvDigits,
			billing: cleanBilling,
		})
	}

	return (
		<form id={formId} onSubmit={handleSubmit} className="space-y-3">
			{/* ── Card Details ── */}
			<div className="relative w-full">
				<input
					type="text"
					inputMode="numeric"
					autoComplete="cc-number"
					id="floating-card-number"
					placeholder="Card Number"
					value={cardNumber}
					onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
					disabled={isSubmitting}
					className="peer w-full h-14 px-4 pt-5 pb-2 pr-14 rounded-lg border bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] text-base placeholder-transparent focus:outline-none focus:ring-2 focus:ring-[#EF4444] focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
				/>
				<label
					htmlFor="floating-card-number"
					className="absolute left-4 text-[#888888] transition-all duration-200 pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-xs peer-focus:text-[#BABABB] top-2 -translate-y-0 text-xs peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:-translate-y-0 peer-[:not(:placeholder-shown)]:text-xs"
				>
					Card Number
				</label>
				<div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
					<PaymentIcon type={detectedBrand} format="flatRounded" width={36} />
				</div>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<FloatingInput
					label="Expiration"
					inputMode="numeric"
					autoComplete="cc-exp"
					value={expiry}
					onChange={(e) => setExpiry(formatExpiry(e.target.value))}
					disabled={isSubmitting}
				/>
				<FloatingInput
					label="CVV"
					inputMode="numeric"
					autoComplete="cc-csc"
					maxLength={4}
					value={cvv}
					onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
					disabled={isSubmitting}
				/>
			</div>

			{/* ── Billing Address ── */}
			<div className="pt-2">
				<p className="font-figtree font-semibold text-[13px] text-[#BABABB] mb-2">
					Billing Address
				</p>

				<div className="space-y-3">
					<div className="grid grid-cols-2 gap-3">
						<FloatingInput
							label="First Name"
							autoComplete="given-name"
							value={billing.firstName}
							onChange={(e) => updateBilling('firstName', e.target.value)}
							disabled={isSubmitting}
						/>
						<FloatingInput
							label="Last Name"
							autoComplete="family-name"
							value={billing.lastName}
							onChange={(e) => updateBilling('lastName', e.target.value)}
							disabled={isSubmitting}
						/>
					</div>

					<FloatingInput
						label="Address"
						autoComplete="address-line1"
						value={billing.address1}
						onChange={(e) => updateBilling('address1', e.target.value)}
						disabled={isSubmitting}
					/>

					<FloatingInput
						label="Address Line 2"
						autoComplete="address-line2"
						value={billing.address2 || ''}
						onChange={(e) => updateBilling('address2', e.target.value)}
						disabled={isSubmitting}
					/>

					<div className="grid grid-cols-2 gap-3">
						<FloatingInput
							label="City"
							autoComplete="address-level2"
							value={billing.city}
							onChange={(e) => updateBilling('city', e.target.value)}
							disabled={isSubmitting}
						/>
						<FloatingInput
							label={addrConfig.stateLabel}
							autoComplete="address-level1"
							maxLength={addrConfig.stateMaxLength}
							value={billing.state}
							onChange={(e) => updateBilling('state', e.target.value.toUpperCase())}
							disabled={isSubmitting}
						/>
					</div>

					<div className="grid grid-cols-2 gap-3">
						<FloatingInput
							label={addrConfig.postalLabel}
							inputMode={addrConfig.postalInputMode}
							autoComplete="postal-code"
							maxLength={addrConfig.postalMaxLength}
							value={billing.zip}
							onChange={(e) => updateBilling('zip', formatPostalCode(e.target.value, billing.country || 'US'))}
							disabled={isSubmitting}
						/>
						<div className="relative w-full">
							<select
								id="floating-country"
								autoComplete="country"
								value={billing.country || 'US'}
								onChange={(e) => handleCountryChange(e.target.value as BillingCountry)}
								disabled={isSubmitting}
								className="peer w-full h-14 px-4 pt-5 pb-2 rounded-lg border bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] text-base focus:outline-none focus:ring-2 focus:ring-[#EF4444] focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-colors appearance-none"
							>
								{BILLING_COUNTRIES.map((c) => (
									<option key={c.value} value={c.value}>{c.label}</option>
								))}
							</select>
							<label
								htmlFor="floating-country"
								className="absolute left-4 top-2 text-xs text-[#BABABB] pointer-events-none"
							>
								Country
							</label>
							<div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
								<svg className="h-4 w-4 text-[#BABABB]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Hidden submit button — the parent controls submission via its own button */}
			<button type="submit" className="hidden" />
		</form>
	)
}
